import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, CreditCard, Loader2, MapPin, MessageSquare, Package, Plus, RefreshCw, Search, Trash2, Truck, User } from 'lucide-react';
import { orderApi, storeApi, warehouseApi, type Order, type Product } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useNotification } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';

export function createManualOrderForm(order?: Order, storeId = "") {
  if (order) {
    const shipping = (order.shippingAddress || {}) as Record<string, string>;
    const billing = (order.billingAddress || {}) as Record<string, string>;
    const items = (order.items || []) as Array<Record<string, unknown>>;

    // Parse customer name into first/last
    const nameParts = (order.customerName || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return {
      storeId: order.store?.id || '',
      customerFirstName: firstName,
      customerLastName: lastName,
      customerEmail: order.customerEmail || '',
      customerPhone: order.customerPhone || '',
      shippingAddressEdited: true,
      shippingAddress: {
        first_name: shipping.first_name || firstName,
        last_name: shipping.last_name || lastName,
        company: shipping.company || '',
        address_1: shipping.address_1 || '',
        address_2: shipping.address_2 || '',
        city: shipping.city || '',
        postcode: shipping.postcode || '',
        country: shipping.country || 'PL',
        phone: shipping.phone || order.customerPhone || '',
      },
      sameAsShipping: true,
      billingAddress: {
        first_name: billing.first_name || firstName,
        last_name: billing.last_name || lastName,
        company: billing.company || '',
        address_1: billing.address_1 || '',
        address_2: billing.address_2 || '',
        city: billing.city || '',
        postcode: billing.postcode || '',
        country: billing.country || 'PL',
        phone: billing.phone || '',
        email: billing.email || order.customerEmail || '',
      },
      items: items.map(item => ({
        name: String(item.name || ''),
        quantity: Number(item.quantity) || 1,
        price: String(item.price || '0'),
        total: String(item.total || '0'),
        total_tax: String(item.total_tax || '0'),
        sku: String(item.sku || ''),
        productId: (item.product_id as string) || (item.productId as string) || null,
        isCustom: !item.product_id && !item.productId,
      })),
      shippingMethod: order.shippingMethod || '',
      shippingMethodId: '',
      shippingCost: String((order.totals as Record<string, unknown>)?.shippingTotal || '0'),
      shippingTax: String((order.totals as Record<string, unknown>)?.shippingTax || '0'),
      paymentMethod: order.paymentMethod || '',
      isPaid: false,
      paczkomatNumber: '',
      statusId: null,
      notes: '',
      internalNotes: '',
    };
  }
  return {
    storeId,
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    customerPhone: '',
    shippingAddressEdited: false, // Track if user manually edited shipping address
    shippingAddress: {
      first_name: '',
      last_name: '',
      company: '',
      address_1: '',
      address_2: '',
      city: '',
      postcode: '',
      country: 'PL',
      phone: '',
    },
    sameAsShipping: true,
    billingAddress: {
      first_name: '',
      last_name: '',
      company: '',
      address_1: '',
      address_2: '',
      city: '',
      postcode: '',
      country: 'PL',
      phone: '',
      email: '',
    },
    items: [] as Array<{
      name: string;
      quantity: number;
      price: string;
      total: string;
      total_tax: string;
      sku: string;
      productId: string | null;
      isCustom: boolean; // true = ręcznie wpisany produkt
    }>,
    shippingMethod: '',
    shippingMethodId: '',
    shippingCost: '0',
    shippingTax: '0',
    paymentMethod: '',
    isPaid: false,
    paczkomatNumber: '',
    statusId: null as string | null,
    notes: '',
    internalNotes: '',
  };
}

export function CreateOrderModal({ sourceOrder, initialStoreId = '', onClose, onCreated }: {
  sourceOrder?: Order;
  initialStoreId?: string;
  onClose: () => void;
  onCreated: (order: Order) => void;
}) {
  const { showSuccess, showError, showWarning } = useNotification();
  const { tenant, user } = useAuthStore();
  const { data: stores = [] } = useQuery({
    queryKey: ['create-order-stores', tenant?.id, user?.id],
    enabled: Boolean(tenant?.id && user?.id),
    queryFn: async () => (await storeApi.getAll()).data,
  });
  const [createOrderLoading, setCreateOrderLoading] = useState(false);
  const [shippingMethods, setShippingMethods] = useState<Array<{ id: string; title: string }>>([]);
  const [paymentGateways, setPaymentGateways] = useState<Array<{ id: string; title: string }>>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [createOrderForm, setCreateOrderForm] = useState(() => createManualOrderForm(sourceOrder, initialStoreId));

  // Warehouse products for product selector
  const [warehouseProducts, setWarehouseProducts] = useState<Product[]>([]);
  const [loadingWarehouseProducts, setLoadingWarehouseProducts] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [showWarehouseSearch, setShowWarehouseSearch] = useState(false);

  // Load shipping methods (from store mappings) and payment gateways for selected store
  const loadShippingAndPaymentMethods = async (storeId: string) => {
    if (!storeId) {
      setShippingMethods([]);
      setPaymentGateways([]);
      return;
    }

    setLoadingMethods(true);
    try {
      // Use mapped shipping methods from store settings instead of raw WooCommerce methods
      const store = stores.find(s => s.id === storeId);
      const mappings = store?.settings?.shippingMethodMappings || [];

      if (mappings.length > 0) {
        // Deduplicate by wooMethodTitle to avoid repeated entries
        const seen = new Set<string>();
        const mapped = mappings
          .filter(m => {
            if (seen.has(m.wooMethodTitle)) return false;
            seen.add(m.wooMethodTitle);
            return true;
          })
          .map(m => ({ id: m.wooMethodId, title: m.wooMethodTitle }));
        setShippingMethods(mapped);
      } else {
        // Fallback: fetch from WooCommerce API if no mappings configured
        try {
          const shippingRes = await storeApi.getShippingMethods(storeId);
          setShippingMethods(shippingRes.data);
        } catch {
          setShippingMethods([]);
        }
      }

      // Payment gateways still come from WooCommerce
      const paymentRes = await storeApi.getPaymentGateways(storeId);
      setPaymentGateways(paymentRes.data);
      setCreateOrderForm(previous => {
        if (previous.storeId !== storeId) return previous;
        const gateway = paymentRes.data.find(item => item.id === previous.paymentMethod);
        return gateway ? { ...previous, paymentMethod: gateway.title } : previous;
      });
    } catch (err) {
      console.error('Failed to load shipping/payment methods:', err);
      setShippingMethods([]);
      setPaymentGateways([]);
    } finally {
      setLoadingMethods(false);
    }
  };

  // Load warehouse products for product selector
  const loadWarehouseProducts = async (search?: string) => {
    setLoadingWarehouseProducts(true);
    try {
      const { data } = await warehouseApi.getProducts({
        search: search || undefined,
        isActive: 'true',
        limit: 50,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      setWarehouseProducts(data.products);
    } catch (err) {
      console.error('Failed to load warehouse products:', err);
      setWarehouseProducts([]);
    } finally {
      setLoadingWarehouseProducts(false);
    }
  };

  // Select product from warehouse - adds to order items list
  const selectProductFromWarehouse = (product: Product) => {
    setCreateOrderForm(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          name: product.name,
          quantity: 1,
          price: product.sellingPrice?.toString() || '0',
          total: Number(product.sellingPrice || 0).toFixed(2),
          total_tax: '0',
          sku: product.sku || '',
          productId: product.id,
          isCustom: false,
        },
      ],
    }));
    setShowWarehouseSearch(false);
    setProductSearchQuery('');
  };

  // Add manual product to create order form
  const addProductToOrder = () => {
    setCreateOrderForm(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          name: '',
          quantity: 1,
          price: '0',
          total: '0',
          total_tax: '0',
          sku: '',
          productId: null,
          isCustom: true,
        },
      ],
    }));
  };

  // Remove product from create order form
  const removeProductFromOrder = (index: number) => {
    setCreateOrderForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // Update product in create order form
  const updateProductInOrder = (index: number, field: string, value: string | number) => {
    setCreateOrderForm(prev => {
      const newItems = [...prev.items];
      const item = { ...newItems[index], [field]: value };

      // Auto-calculate total when price or quantity changes
      if (field === 'price' || field === 'quantity') {
        const price = parseFloat(field === 'price' ? String(value) : item.price) || 0;
        const quantity = field === 'quantity' ? Number(value) : item.quantity;
        item.total = (price * quantity).toFixed(2);
      } else if (field === 'total') {
        // Reverse calc: użytkownik edytuje sumę → przelicz cenę jednostkową.
        // Wymagamy quantity > 0 (inaczej dzielenie przez zero). Cena zaokrąglana
        // do 2 miejsc po przecinku.
        const total = parseFloat(String(value)) || 0;
        const quantity = Number(item.quantity) || 0;
        if (quantity > 0) {
          item.price = (total / quantity).toFixed(2);
        }
      }

      newItems[index] = item;
      return { ...prev, items: newItems };
    });
  };

  // Calculate order total
  const calculateOrderTotal = () => {
    const itemsTotal = createOrderForm.items.reduce((sum, item) => {
      const net = parseFloat(item.total) || 0;
      const tax = parseFloat(item.total_tax) || 0;
      return sum + net + tax;
    }, 0);
    const shippingTotal = (parseFloat(createOrderForm.shippingCost) || 0) + (parseFloat(createOrderForm.shippingTax) || 0);
    return (itemsTotal + shippingTotal).toFixed(2);
  };

  // Handle create order submit
  const handleCreateOrder = async () => {
    // Collect all validation errors at once
    const validationErrors: string[] = [];
    if (!createOrderForm.storeId) validationErrors.push('Wybierz sklep');
    if (!createOrderForm.customerFirstName.trim()) validationErrors.push('Podaj imię klienta');
    if (!createOrderForm.customerLastName.trim()) validationErrors.push('Podaj nazwisko klienta');
    if (!createOrderForm.customerEmail.trim()) validationErrors.push('Podaj adres e-mail klienta');
    if (!createOrderForm.customerPhone.trim()) validationErrors.push('Podaj numer telefonu klienta');
    if (!createOrderForm.shippingMethod.trim()) validationErrors.push('Wybierz metodę dostawy');
    if (!createOrderForm.paymentMethod.trim()) validationErrors.push('Wybierz metodę płatności');

    // Address validation
    const methodLower = createOrderForm.shippingMethod.toLowerCase();
    const isPaczkomat = methodLower.includes('paczkomat') || methodLower.includes('locker') || methodLower.includes('automat');
    if (isPaczkomat && !createOrderForm.paczkomatNumber.trim()) validationErrors.push('Podaj numer Paczkomatu');
    if (!isPaczkomat && !createOrderForm.shippingAddress.address_1.trim()) validationErrors.push('Podaj adres dostawy (ulicę)');
    if (!createOrderForm.shippingAddress.postcode.trim()) validationErrors.push('Podaj kod pocztowy');
    if (!createOrderForm.shippingAddress.city.trim()) validationErrors.push('Podaj miasto');
    if (createOrderForm.items.length === 0) validationErrors.push('Dodaj przynajmniej jeden produkt');
    if (createOrderForm.items.some(item => !item.name.trim())) validationErrors.push('Wszystkie produkty muszą mieć nazwę');
    if (createOrderForm.items.some(item => !item.quantity || Number(item.quantity) < 1)) validationErrors.push('Każdy produkt musi mieć ilość większą niż 0');

    if (validationErrors.length > 0) {
      showWarning('Brakujące dane', validationErrors.join('\n'));
      return;
    }

    try {
      setCreateOrderLoading(true);

      // Combine first and last name for backend
      const customerName = `${createOrderForm.customerFirstName.trim()} ${createOrderForm.customerLastName.trim()}`.trim();

      const { data: newOrder } = await orderApi.create({
        storeId: createOrderForm.storeId,
        customerName,
        customerEmail: createOrderForm.customerEmail.trim() || undefined,
        customerPhone: createOrderForm.customerPhone.trim() || undefined,
        shippingAddress: createOrderForm.shippingAddress,
        billingAddress: createOrderForm.sameAsShipping ? undefined : createOrderForm.billingAddress,
        sameAsShipping: createOrderForm.sameAsShipping,
        items: createOrderForm.items.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.total,
          total_tax: item.total_tax,
          sku: item.sku,
          productId: item.productId,
        })),
        shippingMethod: createOrderForm.shippingMethod.trim() || undefined,
        shippingMethodId: createOrderForm.shippingMethodId || undefined,
        shippingCost: createOrderForm.shippingCost,
        shippingTax: createOrderForm.shippingTax,
        paymentMethod: createOrderForm.paymentMethod.trim() || undefined,
        isPaid: createOrderForm.isPaid || undefined,
        paczkomatNumber: createOrderForm.paczkomatNumber.trim() || undefined,
        notes: createOrderForm.notes.trim() || undefined,
        internalNotes: createOrderForm.internalNotes.trim() || undefined,
      });

      // L5: Show warning if WooCommerce sync failed but order was created locally
      if ((newOrder as any).warning) {
        showError('Uwaga', (newOrder as any).warning);
      }
      showSuccess('ORDER_CREATE', { message: `Zamówienie ${newOrder.orderNumber} zostało utworzone` });
      onCreated(newOrder);
      onClose();
    } catch (err: unknown) {
      console.error('Failed to create order:', err);
      // Extract meaningful error — handle Zod validation details from backend
      const axiosErr = err as { response?: { data?: { error?: string; message?: string; details?: Array<{ path: string; message: string }> } } };
      const details = axiosErr?.response?.data?.details;
      if (details && Array.isArray(details) && details.length > 0) {
        // Zod validation errors from backend — show each field error
        const fieldErrors = details.map(d => `${d.path}: ${d.message}`).join('\n');
        showWarning('Nieprawidłowe dane', fieldErrors);
      } else {
        showError(err, 'order');
      }
    } finally {
      setCreateOrderLoading(false);
    }
  };


  useEffect(() => {
    if (stores.length) void loadShippingAndPaymentMethods(createOrderForm.storeId);
  }, [stores, createOrderForm.storeId]);

  return (
    <Modal isOpen onClose={onClose} title="Nowe zamówienie ręczne" size="2xl"
      closeOnEscape={!createOrderLoading} closeOnBackdrop={!createOrderLoading} hideCloseButton={createOrderLoading}
      bodyClassName="space-y-6" footer={(
            <div className="flex items-center gap-2 p-6 border-t border-border bg-surface-raised">
              <Button
                variant="primary"
                onClick={handleCreateOrder}
                disabled={createOrderLoading}
                loading={createOrderLoading}
                leftIcon={createOrderLoading ? undefined : <Check />}
              >
                {createOrderLoading ? 'Tworzenie...' : 'Utwórz zamówienie'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => onClose()}
                disabled={createOrderLoading}
              >
                Anuluj
              </Button>
            </div>
      )}>
              {/* Store selection - hidden when store pre-selected from filter or only one active store */}
              {!initialStoreId && stores.filter(s => s.isActive !== false).length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Sklep <span className="text-danger">*</span>
                  </label>
                  <Select
                    value={createOrderForm.storeId}
                    onChange={(e) => {
                      const storeId = e.target.value;
                      setCreateOrderForm(prev => ({ ...prev, storeId, shippingMethod: '', shippingMethodId: '', paymentMethod: '', statusId: null }));
                    }}
                    className="input w-full"
                  >
                    <option value="">Wybierz sklep</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </Select>
                </div>
              )}

              {/* Shipping & Payment */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-surface-hover rounded-lg p-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-4 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Dostawa
                    {loadingMethods && <RefreshCw className="w-3 h-3 animate-spin text-text-muted" />}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">Metoda dostawy <span className="text-danger">*</span></label>
                      {shippingMethods.length > 0 ? (
                        <Select
                          value={createOrderForm.shippingMethod}
                          onChange={(e) => {
                            const selectedTitle = e.target.value;
                            const selectedMethodObj = shippingMethods.find(m => m.title === selectedTitle);
                            setCreateOrderForm(prev => ({ ...prev, shippingMethod: selectedTitle, shippingMethodId: selectedMethodObj?.id || '', paczkomatNumber: '' }));
                          }}
                          className="input w-full"
                          disabled={loadingMethods}
                        >
                          <option value="">Wybierz metodę dostawy</option>
                          {shippingMethods.map(method => (
                            <option key={method.id} value={method.title}>{method.title}</option>
                          ))}
                        </Select>
                      ) : (
                        <input
                          type="text"
                          value={createOrderForm.shippingMethod}
                          onChange={(e) => setCreateOrderForm(prev => ({ ...prev, shippingMethod: e.target.value }))}
                          className="input w-full"
                          placeholder={loadingMethods ? 'Ładowanie...' : 'np. Kurier, Paczkomat'}
                          disabled={loadingMethods}
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">Koszt dostawy</label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={createOrderForm.shippingCost}
                        onChange={(e) => setCreateOrderForm(prev => ({ ...prev, shippingCost: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-surface-hover rounded-lg p-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-4 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Płatność
                    {loadingMethods && <RefreshCw className="w-3 h-3 animate-spin text-text-muted" />}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">Metoda płatności <span className="text-danger">*</span></label>
                      <Select
                        value={createOrderForm.paymentMethod}
                        onChange={(e) => {
                          const method = e.target.value;
                          const methodLower = method.toLowerCase();
                          const isCOD = methodLower.includes('pobranie') || methodLower.includes('przy odbiorze') || methodLower.includes('cod');
                          const isOnline = methodLower.includes('przelewy24') || methodLower.includes('p24') || methodLower.includes('blik') || methodLower.includes('karta');
                          const isTransfer = methodLower === 'przelew tradycyjny';
                          setCreateOrderForm(prev => ({
                            ...prev,
                            paymentMethod: method,
                            isPaid: isCOD ? false : (isOnline || isTransfer) ? true : prev.isPaid,
                          }));
                        }}
                        className="input w-full"
                        disabled={loadingMethods}
                      >
                        <option value="">Wybierz metodę płatności <span className="text-danger">*</span></option>
                        {paymentGateways.length > 0 ? (
                          <>
                            {paymentGateways.map(gateway => (
                              <option key={gateway.id} value={gateway.title}>{gateway.title}</option>
                            ))}
                            {!paymentGateways.some(g => g.title.toLowerCase() === 'przelew tradycyjny') && (
                              <option value="Przelew tradycyjny">Przelew tradycyjny</option>
                            )}
                          </>
                        ) : (
                          <>
                            <option value="Przelewy24">Przelewy24</option>
                            <option value="BLIK">BLIK</option>
                            <option value="Karta płatnicza">Karta płatnicza</option>
                            <option value="Przelew tradycyjny">Przelew tradycyjny</option>
                            <option value="Pobranie">Pobranie</option>
                          </>
                        )}
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">Status płatności <span className="text-danger">*</span></label>
                      {(() => {
                        const methodLower = createOrderForm.paymentMethod.toLowerCase();
                        const isCOD = methodLower.includes('pobranie') || methodLower.includes('przy odbiorze') || methodLower.includes('cod');
                        const isOnline = methodLower.includes('przelewy24') || methodLower.includes('p24') || methodLower.includes('blik') || methodLower.includes('karta');
                        const isLocked = isCOD || isOnline;
                        return (
                          <Select
                            value={createOrderForm.isPaid ? 'paid' : 'unpaid'}
                            onChange={(e) => setCreateOrderForm(prev => ({ ...prev, isPaid: e.target.value === 'paid' }))}
                            className="input w-full"
                            disabled={isLocked}
                          >
                            <option value="paid">Opłacone</option>
                            <option value="unpaid">Nieopłacone</option>
                          </Select>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer data & Shipping address */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-surface-hover rounded-lg p-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-4 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Dane klienta
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">
                        Imię <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        value={createOrderForm.customerFirstName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCreateOrderForm(prev => ({
                            ...prev,
                            customerFirstName: value,
                            shippingAddress: prev.shippingAddressEdited
                              ? prev.shippingAddress
                              : { ...prev.shippingAddress, first_name: value }
                          }));
                        }}
                        className="input w-full"
                        placeholder="Jan"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">
                        Nazwisko <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        value={createOrderForm.customerLastName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCreateOrderForm(prev => ({
                            ...prev,
                            customerLastName: value,
                            shippingAddress: prev.shippingAddressEdited
                              ? prev.shippingAddress
                              : { ...prev.shippingAddress, last_name: value }
                          }));
                        }}
                        className="input w-full"
                        placeholder="Kowalski"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-secondary font-medium mb-1">Email <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        value={createOrderForm.customerEmail}
                        onChange={(e) => setCreateOrderForm(prev => ({ ...prev, customerEmail: e.target.value }))}
                        className="input w-full ring-1 ring-gray-600"
                        placeholder="jan@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-secondary font-medium mb-1">Telefon <span className="text-danger">*</span></label>
                      <input
                        type="tel"
                        value={createOrderForm.customerPhone}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCreateOrderForm(prev => ({
                            ...prev,
                            customerPhone: value,
                            shippingAddress: prev.shippingAddressEdited
                              ? prev.shippingAddress
                              : { ...prev.shippingAddress, phone: value }
                          }));
                        }}
                        className="input w-full ring-1 ring-gray-600"
                        placeholder="+48 123 456 789"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-surface-hover rounded-lg p-4">
                  <h3 className="text-sm font-medium text-text-secondary mb-4 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Adres dostawy
                    {createOrderForm.shippingAddressEdited && (
                      <span className="text-xs text-text-muted font-normal ml-2">(edytowany ręcznie)</span>
                    )}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {(() => {
                      const methodLower = createOrderForm.shippingMethod.toLowerCase();
                      const isPaczkomat = methodLower.includes('paczkomat') || methodLower.includes('locker') || methodLower.includes('automat');
                      return isPaczkomat ? (
                        <div>
                          <label className="block text-xs text-text-secondary mb-1">
                            Numer Paczkomatu <span className="text-danger">*</span>
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={createOrderForm.paczkomatNumber}
                              onChange={(e) => setCreateOrderForm(prev => ({ ...prev, paczkomatNumber: e.target.value.toUpperCase() }))}
                              className="input w-full"
                              placeholder="np. WAW04A"
                            />
                            <a
                              href="https://inpost.pl/znajdz-paczkomat"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center px-2.5 shrink-0 rounded-btn bg-surface-hover/60 text-text-primary border border-accent-primary/30 hover:border-accent-primary/50 hover:bg-surface-muted/60 backdrop-blur-sm transition-colors"
                              title="Znajdź Paczkomat na mapie"
                            >
                              <Search className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      ) : null;
                    })()}
                    <div className={(() => {
                      const methodLower = createOrderForm.shippingMethod.toLowerCase();
                      const isPaczkomat = methodLower.includes('paczkomat') || methodLower.includes('locker') || methodLower.includes('automat');
                      return isPaczkomat ? '' : 'col-span-2';
                    })()}>
                      <label className="block text-xs text-text-secondary mb-1">Adres{(() => {
                        const methodLower = createOrderForm.shippingMethod.toLowerCase();
                        const isPaczkomat = methodLower.includes('paczkomat') || methodLower.includes('locker') || methodLower.includes('automat');
                        return !isPaczkomat ? <span className="text-danger"> *</span> : null;
                      })()}</label>
                      <input
                        type="text"
                        value={createOrderForm.shippingAddress.address_1}
                        onChange={(e) => setCreateOrderForm(prev => ({
                          ...prev,
                          shippingAddress: { ...prev.shippingAddress, address_1: e.target.value }
                        }))}
                        className="input w-full"
                        placeholder="ul. Przykładowa 1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">Kod pocztowy <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        value={createOrderForm.shippingAddress.postcode}
                        onChange={(e) => setCreateOrderForm(prev => ({
                          ...prev,
                          shippingAddress: { ...prev.shippingAddress, postcode: e.target.value }
                        }))}
                        className="input w-full"
                        placeholder="00-000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">Miasto <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        value={createOrderForm.shippingAddress.city}
                        onChange={(e) => setCreateOrderForm(prev => ({
                          ...prev,
                          shippingAddress: { ...prev.shippingAddress, city: e.target.value }
                        }))}
                        className="input w-full"
                        placeholder="Warszawa"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-surface-hover rounded-lg p-4">
                <h3 className="text-sm font-medium text-text-secondary mb-4 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Notatki
                </h3>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Notatka wewnętrzna</label>
                  <textarea
                    value={createOrderForm.internalNotes}
                    onChange={(e) => setCreateOrderForm(prev => ({ ...prev, internalNotes: e.target.value }))}
                    className="input w-full resize-none"
                    rows={2}
                    placeholder="Notatka widoczna tylko dla zespołu..."
                  />
                </div>
              </div>

              {/* Products */}
              <div className="bg-surface-hover rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-text-secondary flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Produkty <span className="text-danger">*</span>
                  </h3>
                  <div className="flex gap-2">
                    <Button
                      variant={showWarehouseSearch ? 'secondary' : 'primary'}
                      size="sm"
                      className={showWarehouseSearch ? 'border-accent-primary/50' : ''}
                      onClick={() => {
                        if (!showWarehouseSearch) {
                          setProductSearchQuery('');
                          loadWarehouseProducts();
                        }
                        setShowWarehouseSearch(!showWarehouseSearch);
                      }}
                      title="Wybierz produkt z magazynu"
                      leftIcon={<Package />}
                    >
                      Z magazynu
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => addProductToOrder()}
                      title="Dodaj produkt ręcznie"
                      leftIcon={<Plus />}
                    >
                      Ręcznie
                    </Button>
                  </div>
                </div>

                {/* Inline warehouse product search accordion */}
                {showWarehouseSearch && (
                  <div className="mb-4 bg-surface-raised/60 rounded-lg border border-border-subtle overflow-hidden">
                    <div className="p-3">
                      <div className="relative">
                        <input
                          type="text"
                          value={productSearchQuery}
                          onChange={(e) => {
                            setProductSearchQuery(e.target.value);
                            loadWarehouseProducts(e.target.value);
                          }}
                          className="input w-full text-sm pl-10"
                          placeholder="Szukaj po nazwie lub SKU..."
                          autoFocus
                        />
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                        {loadingWarehouseProducts && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-text-muted" />
                        )}
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto border-t border-border-subtle">
                      {loadingWarehouseProducts && warehouseProducts.length === 0 ? (
                        <div className="p-4 text-center text-text-muted">
                          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
                          <span className="text-xs">Ładowanie...</span>
                        </div>
                      ) : warehouseProducts.length === 0 ? (
                        <div className="p-4 text-center text-text-muted">
                          <span className="text-xs">Brak produktów w magazynie</span>
                        </div>
                      ) : (
                        warehouseProducts.map((product, idx) => (
                          <button
                            key={product.id}
                            onClick={() => selectProductFromWarehouse(product)}
                            className={`w-full px-3 py-2 text-left hover:bg-surface-hover/50 transition-colors flex items-center justify-between ${idx !== warehouseProducts.length - 1 ? 'border-b border-border-subtle' : ''}`}
                          >
                            <div>
                              <div className="text-sm text-text-secondary">{product.name}</div>
                              <div className="text-xs text-text-muted">
                                {product.sku && <span>SKU: {product.sku} • </span>}
                                Stan: {product.quantity}
                              </div>
                            </div>
                            <div className="text-sm text-text-secondary tabular-nums ml-3 whitespace-nowrap">
                              {product.sellingPrice ? `${Number(product.sellingPrice).toFixed(2)} zł` : '-'}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {createOrderForm.items.length === 0 && !showWarehouseSearch ? (
                  <div className="flex flex-col items-center justify-center py-8 text-text-muted min-h-[200px]">
                    <Package className="w-10 h-10 mb-2 opacity-50" />
                    <p className="text-sm">Brak produktów. Wybierz produkt z magazynu lub dodaj ręcznie.</p>
                  </div>
                ) : createOrderForm.items.length > 0 ? (
                  <div className="space-y-3">
                    {createOrderForm.items.map((item, index) => (
                      <div key={index} className="bg-surface-muted rounded-lg p-3">
                        <div className="flex items-start gap-3">
                          <div className="flex-1 grid grid-cols-12 gap-3">
                            <div className="col-span-4 relative">
                              <label className="block text-xs text-text-secondary mb-1">
                                Nazwa produktu
                                {!item.isCustom && (
                                  <span className="ml-2 text-accent-primary text-[10px]">(z magazynu)</span>
                                )}
                              </label>
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) => {
                                  updateProductInOrder(index, 'name', e.target.value);
                                  if (!item.isCustom) {
                                    setCreateOrderForm(prev => {
                                      const newItems = [...prev.items];
                                      newItems[index] = { ...newItems[index], isCustom: true, productId: null };
                                      return { ...prev, items: newItems };
                                    });
                                  }
                                }}
                                className="input w-full text-sm"
                                placeholder="Nazwa produktu"
                                readOnly={!item.isCustom && !!item.productId}
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-text-secondary mb-1">SKU</label>
                              <input
                                type="text"
                                value={item.sku}
                                onChange={(e) => updateProductInOrder(index, 'sku', e.target.value)}
                                className="input w-full text-sm"
                                placeholder="SKU"
                                readOnly={!item.isCustom && !!item.productId}
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-text-secondary mb-1">Ilość</label>
                              <input
                                type="number"
                                min="0"
                                value={item.quantity === 0 || item.quantity === undefined ? '' : item.quantity}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  // PB7 (2026-04-30): puste pole → 0 zamiast wymuszanej 1.
                                  // User chce móc wymazać wartość bez ograniczeń.
                                  const parsed = raw === '' ? 0 : Math.max(0, parseInt(raw) || 0);
                                  updateProductInOrder(index, 'quantity', parsed);
                                }}
                                className="input w-full text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="0"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-text-secondary mb-1">Cena jedn.</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.price}
                                onChange={(e) => updateProductInOrder(index, 'price', e.target.value)}
                                className="input w-full text-sm"
                                placeholder="0.00"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-text-secondary mb-1">Suma</label>
                              {/* PB7 (2026-04-30): edytowalna - wpisanie sumy auto-przelicza cenę
                                  jednostkową gdy ilość > 0. Dwukierunkowa kalkulacja. */}
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.total}
                                onChange={(e) => updateProductInOrder(index, 'total', e.target.value)}
                                className="input w-full text-sm"
                                placeholder="0.00"
                                title={Number(item.quantity) === 0 ? 'Najpierw ustaw ilość, żeby auto-przeliczyć cenę jednostkową' : 'Wpisanie sumy przeliczy cenę jednostkową'}
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => removeProductFromOrder(index)}
                            className="p-2 text-danger hover:text-danger hover:bg-red-400/10 rounded-lg transition-colors mt-5"
                            title="Usuń produkt"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Shipping cost line + Order summary */}
                {createOrderForm.items.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-dark-500">
                    {createOrderForm.shippingMethod && (
                      <div className="flex items-center justify-between text-sm text-text-secondary mb-2">
                        <span className="flex items-center gap-2">
                          <Truck className="w-3.5 h-3.5" />
                          {createOrderForm.shippingMethod}
                        </span>
                        <span>{(parseFloat(createOrderForm.shippingCost) || 0).toFixed(2)} zł</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-lg font-bold text-text-primary">
                      <span>Suma</span>
                      <span className="text-accent-primary">{calculateOrderTotal()} zł</span>
                    </div>
                  </div>
                )}
              </div>

    </Modal>
  );
}
